import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { VideoPlayer } from "@/features/player/VideoPlayer";
import { pickSource } from "@/features/player/sourceLoader";
import { EmptyState, Spinner } from "@/components/catalog/TitleGrid";
import { Lock, ShieldAlert } from "lucide-react";
import { useBilling } from "@/context/BillingContext";
import { useGuest } from "@/context/GuestContext";
import { API, api, formatApiError, PROFILE_KEY, TOKEN_KEY } from "@/lib/api";
import { watchPath } from "@/lib/play";

const GUEST_PROGRESS = { entries: [], resume: { episode_id: null, position: 0 } };
const NUDGE_AFTER_SEC = 60;

const sendKeepalive = (body) => {
  const token = localStorage.getItem(TOKEN_KEY);
  fetch(`${API}/watch/progress`, {
    method: "POST",
    keepalive: true,
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-Profile-Id": localStorage.getItem(PROFILE_KEY) || "", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  }).catch(() => {});
};

export default function Watch() {
  const { slug, episodeId } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const [source, setSource] = useState(null);
  const [locked, setLocked] = useState(false);
  const { openUpgrade } = useBilling();
  const { isGuest, nudge } = useGuest();
  const watched = useRef(0);

  useEffect(() => {
    setProgress(null);
    Promise.all([api.get(`/catalog/titles/${slug}`), isGuest ? Promise.resolve({ data: GUEST_PROGRESS }) : api.get(`/watch/progress/${slug}`)])
      .then(([t, p]) => {
        setTitle(t.data);
        setProgress(p.data);
        if (t.data.type === "series" && !episodeId) {
          const first = t.data.seasons[0]?.episodes[0]?.id;
          navigate(watchPath(slug, p.data.resume.episode_id || first), { replace: true });
        }
      })
      .catch((e) => setError(formatApiError(e)));
  }, [slug, episodeId, navigate, isGuest]);

  const episodes = useMemo(() => (title?.seasons || []).flatMap((s) => s.episodes.map((e) => ({ ...e, season: s.number }))), [title]);
  const idx = episodes.findIndex((e) => e.id === episodeId);
  const ep = idx >= 0 ? episodes[idx] : null;
  const next = idx >= 0 ? episodes[idx + 1] || null : null;
  const epKey = ep?.id || null;

  useEffect(() => {
    if (!title || (title.type === "series" && !epKey)) return;
    setSource(null);
    setLocked(false);
    api.get(`/watch/source/${slug}`, { params: epKey ? { episode_id: epKey } : {} })
      .then((r) => setSource(r.data))
      .catch((e) => {
        if (e?.response?.status === 403 && e.response.data?.detail?.code === "upgrade_required") { setLocked(true); openUpgrade(title); } else setError(formatApiError(e));
      });
  }, [title, slug, epKey, openUpgrade]);

  // Guests: nothing is saved server-side; after 60s+ of free playback, show the sign-up nudge once per session.
  useEffect(() => () => { if (isGuest && watched.current >= NUDGE_AFTER_SEC) nudge("playback"); }, [isGuest, nudge, slug, epKey]);

  const onProgress = useCallback((position, duration, opts = {}) => {
    if (isGuest) {
      watched.current = Math.max(watched.current, position);
      if (watched.current >= NUDGE_AFTER_SEC && duration > 0 && position >= duration - 1) nudge("playback");
      return;
    }
    const body = { title_slug: slug, episode_id: epKey, position, duration };
    if (opts.keepalive) sendKeepalive(body);
    else api.post("/watch/progress", body).catch(() => {});
  }, [slug, epKey, isGuest, nudge]);

  const back = useCallback(() => navigate(`/title/${slug}`), [navigate, slug]);
  const playNext = useCallback(() => next && navigate(watchPath(slug, next.id), { replace: true }), [navigate, slug, next]);

  if (error) return <div className="min-h-screen bg-[#050505] px-6 pt-36"><div className="mx-auto max-w-2xl"><EmptyState icon={ShieldAlert} title="Can't play this title" text={error} testId="watch-error" action={<button data-testid="watch-error-back" onClick={() => navigate("/")} className="btn-gold px-6 py-3">Back to Home</button>} /></div></div>;
  if (locked) return <div className="min-h-screen bg-[#050505] px-6 pt-36"><div className="mx-auto max-w-2xl"><EmptyState icon={Lock} title="Premium title" text="Upgrade to Premium to watch this title and the full AETRYX catalog." testId="watch-locked" action={<div className="flex gap-3 justify-center"><button data-testid="watch-locked-upgrade" onClick={() => (isGuest ? openUpgrade(title) : navigate("/pricing"))} className="btn-gold px-6 py-3">{isGuest ? "Sign in" : "See plans"}</button><button onClick={() => navigate(`/title/${slug}`)} className="btn-ghost-gold px-6 py-3">Back</button></div>} /></div></div>;
  if (!title || !progress || !source || (title.type === "series" && !ep)) return <div className="min-h-screen bg-black" data-testid="watch-loading"><Spinner /></div>;

  const media = ep || title;
  const entry = progress.entries.find((e) => (e.episode_id || null) === epKey);
  const startAt = entry && !entry.completed ? entry.position : 0;

  return (
    <div data-testid="watch-page" className="bg-black">
      <VideoPlayer
        key={`${slug}-${epKey}`}
        src={pickSource(source.video_url, source.alt_video_urls)}
        poster={ep?.thumbnail_url || title.backdrop_url}
        subtitles={media.subtitles}
        heading={title.title}
        subheading={ep ? `S${ep.season}:E${ep.number} · ${ep.title}` : `${title.year} · ${title.maturity_rating}`}
        startAt={startAt}
        introStart={media.intro_start}
        introEnd={media.intro_end}
        nextEpisode={next}
        onNext={playNext}
        onProgress={onProgress}
        onBack={back}
      />
    </div>
  );
}
